import { Module, Mutation, Action, VuexModule } from "vuex-module-decorators";
import Vue from "vue";
import { showSuccessSnackbar } from "@/utils/snackbarUtils";
import { Emit } from "@/api/apiHelper";
import { decodeExchangeName, encodeExchangeName } from "@/utils/exchangeUtils";
import {
  Exchange,
  ConnectedExchange,
  NotConnectedExchange,
  ExchangeConnectionStatus,
} from "@/models/Exchange";

/**
 * 🧩 Module which handles the exchanges
 */
@Module
export default class ExchangesModule extends VuexModule {
  // 📦 The user's connected exchanges
  public connectedExchanges: Exchange[] = [];

  // 📦 All the data about the exchanges and their markets
  public exchangesData: any = undefined;

  // 🚩 Flag for whether a new exchange has been connected
  public isExchnageConnected?: boolean;

  // 🚩 Flag for whether for whether the connected exchanges are loading
  public loading: boolean = false;

  public get allExchanges(): Exchange[] {
    return [...this.connectedExchanges, ...this.notConnectedExchanges];
  }

  public get notConnectedExchanges(): Exchange[] {
    const allExchangeNames = Object.keys(
      this.exchangesData
    ).map((rawExchangeName) => decodeExchangeName(rawExchangeName));

    const connectedExchangesNames = this.connectedExchanges.map(
      (exchange: Exchange) => exchange.name
    );

    if (!allExchangeNames || !connectedExchangesNames) {
      return [];
    }

    return allExchangeNames
      .filter(
        (exchangeName: string) =>
          !connectedExchangesNames.includes(exchangeName)
      )
      .map((exchangeName: string) => new NotConnectedExchange(exchangeName));
  }

  // 📦 The number of available exchanges that the user can connect in general
  public get numberOfAvailableExchanges(): number {
    return this.allExchanges ? this.allExchanges.length : 0;
  }

  // 📦 All the available markets across all exchanges without duplicates
  private get allAvailableMarkets(): Set<string> {
    if (!this.exchangesData) {
      return new Set();
    } else {
      let markets: string[] = [];
      Object.values(this.exchangesData).forEach((exchangeData: any) => {
        markets = markets.concat(exchangeData.pairs);
      });
      return new Set(markets);
    }
  }

  // 📦 All the base currencies across all exchanges without duplicates
  public get availableBaseCurrenciesForExchange(): (
    exchangeName: string
  ) => string[] {
    return (exchangeName: string) => {
      return !this.exchangesData
        ? new Array()
        : [
            ...new Set(
              this.exchangesData[encodeExchangeName(exchangeName)].pairs.map(
                (market: string) => market.split(":")[1]
              )
            ),
          ];
    };
  }

  /**
   * 👾 Mutation for changing the loading indicator
   * @param isLoading
   */
  @Mutation
  public setLoading(isLoading: boolean) {
    this.loading = isLoading;
  }

  /**
   * 👾 Mutation for changing the user's connected exchanges
   * @param newExchanges
   */
  @Mutation
  public setConnectedExchanges(newExchanges: Exchange[]) {
    this.connectedExchanges = newExchanges;
  }

  /**
   * 👾 Mutation for changing the exchanges the user can connect
   * @param newAvailableExchanges
   */
  @Mutation
  public setExchangesData(newExchangesData: any) {
    this.exchangesData = newExchangesData;
  }

  /**
   * 👾 Mutation for whether the exchange is being connected successfully
   * @param isConnected
   */
  @Mutation
  public publishExchangeConnected(isConnected?: boolean) {
    this.isExchnageConnected = isConnected;

    if (isConnected) {
      // Reset state so there is no repetance of actions
      this.isExchnageConnected = false;
    }
  }

  /**
   * 🚀 Fetches the user's connected exchanges
   */
  @Action
  public async fetchConnectedExchanges() {
    this.context.commit("setLoading", true);
    try {
      const response = await Vue.prototype.$api.get("/api/v1/connectexchange", {
        with_status_details: true,
      });

      this.context.commit(
        "setConnectedExchanges",
        Object.entries(response.data).map(
          ([exchangeName, details]) =>
            new ConnectedExchange(
              decodeExchangeName(exchangeName),
              (details as any).status
                ? ExchangeConnectionStatus.CONNECTED
                : ExchangeConnectionStatus.NOT_CONNECTED,
              (details as any).days_until_expire_in_seconds
                ? (details as any).days_until_expire_in_seconds / 86400
                : null
            )
        )
      );
    } finally {
      this.context.commit("setLoading", false);
    }
  }

  /**
   * 🚀 Fetches all the available exchanges
   */
  @Action
  public async fetchAllAvailableExchanges() {
    const response = await Vue.prototype.$api.get("/api/v1/getexchanges");
    this.context.commit("setExchangesData", response.data);
  }

  /**
   * 🚀 Deletes a connected user's exchange
   */
  @Action
  public deleteExchangeConnection(index: number) {
    Vue.prototype.$api
      .delete("/api/v1/connectexchange", {
        exchange: encodeExchangeName(this.connectedExchanges[index].name),
      })
      .then((response: any) => {
        const temp = this.connectedExchanges;
        temp.splice(index, 1);
        this.context.commit("setConnectedExchanges", temp);
        showSuccessSnackbar("Your API key was successfully disconnected");
      });
  }

  /**
   * 🚀 Edits an exchange connection
   */
  @Action
  public editExchangeConnection(editRequest: any) {
    return Vue.prototype.$api
      .put(
        "/api/v1/connectexchange",
        {
          exchange: encodeExchangeName(editRequest.exchange),
          api_key: editRequest.apiKey,
          api_secret: editRequest.apiSecret,
          api_passphrase: editRequest.apiPassPhrase,
          access_token: editRequest.apiAccessToken,
          subaccount_name: editRequest.subaccountName,
        },
        Emit.Error
      )
      .then((response: any) => {
        showSuccessSnackbar("Your API details were successfully updated");
      });
  }

  @Action
  private async fetchAllExchanges() {
    return Promise.all([
      this.context.dispatch("fetchAllAvailableExchanges"),
      this.context.dispatch("fetchConnectedExchanges"),
    ]);
  }
}
